import { useEffect, useRef, useState } from 'react'
import { RiCheckLine } from 'react-icons/ri'
import { GButton, GDialog } from 'components/basic-blocks'
import {
	InstructionList,
	SHOPIFY_COPY,
	ShopifyConnectLoading,
	StepRow,
	StepState,
	WaitingRow
} from './shopify-connect-content'
import { useShopifyConnect } from './use-shopify-connect'

interface ShopifyConnectOnboardingProps {
	accountId: string
	assetId: string
	// Fired from the connected state's "Go to dashboard" button to advance the onboarding flow.
	onComplete?: () => void | Promise<void>
}

const primaryBtn =
	'self-start rounded-full h-[44px] px-6 font-medium text-[16px] leading-[22px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)] border border-[#C9ED1A]'
const secondaryBtn = 'self-start rounded-full px-5 h-[44px]'
const blurbCls = 'text-[16px] leading-[22px] text-t-checkbox-unactive'
const titleCls = 'text-[18px] leading-[24px] font-medium'

/**
 * Onboarding Shopify connect wizard — same state machine as the settings wrapper (via
 * useShopifyConnect), sized for the onboarding card (larger headings, 620px container) and finishing
 * with "Go to dashboard" instead of the settings disconnect panel.
 */
export const ShopifyConnectOnboarding = ({ accountId, assetId, onComplete }: ShopifyConnectOnboardingProps) => {
	const s = useShopifyConnect({ accountId, assetId })

	// Pop the "Shopify connected!" completion dialog when the wizard actually finishes — i.e. transitions
	// into `connected` from an active setup step (install/embed), not when landing on an already-connected
	// asset. Mirrors the onboarding-complete popup the other tracking methods show.
	const [showDone, setShowDone] = useState(false)
	const prevStepRef = useRef(s.step)
	useEffect(() => {
		const prev = prevStepRef.current
		if ((prev === 'install' || prev === 'embed') && s.step === 'connected') {
			setShowDone(true)
		}
		prevStepRef.current = s.step
	}, [s.step])

	if (s.loading) return <ShopifyConnectLoading />

	// Final — connected + embed on.
	if (s.step === 'connected' && s.link) {
		return (
			<>
				<GDialog
					open={showDone}
					onClose={() => setShowDone(false)}
					showClose={false}
					maxWidth="xl"
					className="px-[48px] py-[32px]"
				>
					<div className="flex flex-col items-start gap-[16px]">
						<div className="flex w-full flex-col items-start gap-[16px]">
							<h2 className="w-full text-center text-[30px] font-medium leading-[36px] text-t-email-blue">
								Shopify connected!
							</h2>
							<p className="w-full text-center text-[16px] leading-[22px] text-gray-800">
								Your store is connected and paid-click conversion tracking is live.
								<br />
								You’re all set — head to your dashboard.
							</p>
						</div>
						<div className="flex w-full items-center justify-center pt-[8px]">
							<GButton
								label="Go to dashboard"
								color="yellow"
								onClick={() => onComplete?.()}
								className="h-[36px] w-[170px] rounded-full border border-[#bae100] bg-[#eeff9d] px-[12px] py-[8px] text-[14px] leading-[15px] text-on-lemon shadow-[0px_4px_6px_0px_rgba(210,251,12,0.4)]"
							/>
						</div>
					</div>
				</GDialog>

				<div className="flex flex-col gap-4">
					<div className="flex items-center gap-3">
						<RiCheckLine className="h-7 w-7 flex-shrink-0 text-green-500" />
						<span className="text-[20px] font-medium leading-[26px] text-green-600">
							{SHOPIFY_COPY.connected.title}
						</span>
					</div>
					<p className={blurbCls}>{SHOPIFY_COPY.connected.blurb(s.link.shop_domain)}</p>
					<GButton label="Go to dashboard" color="yellow" className={primaryBtn} onClick={() => onComplete?.()} />
				</div>
			</>
		)
	}

	// Steps 1 & 2 — install the app, then turn on the embed.
	const installState: StepState = s.step === 'install' ? 'active' : 'done'
	const embedState: StepState = s.step === 'connected' ? 'done' : s.step === 'embed' ? 'active' : 'todo'
	const installTitle = installState === 'done' ? SHOPIFY_COPY.install.done : SHOPIFY_COPY.install.title
	const embedTitle = embedState === 'done' ? SHOPIFY_COPY.embed.done : SHOPIFY_COPY.embed.title

	return (
		<div className="flex flex-col">
			<StepRow index={1} state={installState} title={installTitle} titleClassName={titleCls}>
				{installState === 'active' && (
					<div className="flex flex-col gap-3">
						<p className={blurbCls}>
							{s.needsReauth ? SHOPIFY_COPY.install.reconnectBlurb : SHOPIFY_COPY.install.blurb}
						</p>
						{s.phase === 'installing' ? (
							<WaitingRow text={SHOPIFY_COPY.install.waiting} onCancel={s.cancel} />
						) : (
							<div className="flex flex-col gap-2">
								<div className="flex flex-wrap items-center gap-3">
									<GButton
										label={s.needsReauth ? SHOPIFY_COPY.install.reconnectButton : SHOPIFY_COPY.install.button}
										color="yellow"
										className={primaryBtn}
										loading={s.busy}
										disabled={s.busy}
										onClick={s.installApp}
									/>
								</div>
								{s.error && <p className="text-[14px] text-[color:var(--error-ink,#D92D20)]">{s.error}</p>}
							</div>
						)}
					</div>
				)}
			</StepRow>

			<StepRow index={2} state={embedState} isLast title={embedTitle} titleClassName={titleCls}>
				{embedState === 'active' && (
					<div className="flex flex-col gap-4">
						<InstructionList items={SHOPIFY_COPY.embed.steps} />
						<p className="rounded-lg bg-bg-plan-fill px-3 py-2 text-[14px] leading-[20px] text-t-checkbox-unactive">
							{SHOPIFY_COPY.embed.note}
						</p>
						{s.phase === 'enabling' ? (
							<WaitingRow text={SHOPIFY_COPY.embed.waiting} onCancel={s.cancel} />
						) : (
							<div className="flex flex-wrap items-center gap-3">
								<GButton label={SHOPIFY_COPY.embed.button} color="yellow" className={primaryBtn} onClick={s.enableEmbed} />
								<GButton
									label="Disconnect"
									color="neutral"
									className={secondaryBtn}
									loading={s.busy}
									disabled={s.busy}
									onClick={s.disconnect}
								/>
							</div>
						)}
					</div>
				)}
				{embedState === 'todo' && <p className={blurbCls}>{SHOPIFY_COPY.embed.todo}</p>}
			</StepRow>
		</div>
	)
}
